import { computed, defineComponent, markRaw, ref } from 'vue'
import type { Router } from 'vue-router'
import type { Store } from 'vuex'
import type { DialogServiceMethods } from 'primevue/dialogservice'
import Button from 'primevue/button'
import IconField from 'primevue/iconfield'
import InputIcon from 'primevue/inputicon'
import InputText from 'primevue/inputtext'
import SidebarTile from './SidebarTile.vue'
import { editSidebarLink } from './edit-sidebar-link-dialog'
import {
  ALL_CATEGORY_ID, NAV_CATEGORIES, RECOMMENDED_CATEGORY_ID, RECOMMENDED_ENTRY_IDS, resolveEntryRouteName, searchNavCatalog,
  type NavCatalogEntry
} from './navCatalog'
import type { SidebarLayoutController } from './useSidebarLayout'
import './sidebar-dialogs.css'

const RECOMMENDED_LIMIT = 10

interface EntryGroup {
  heading?: string
  entries: NavCatalogEntry[]
}

/**
 * The "Add item" picker: every page the person may open, by area, with a search across all of
 * them. Stays open so several items can be added in one go; each is added as soon as its button
 * is pressed, to the end of `sectionId` (or the top level when null).
 */
export function openAddSidebarItem (
  dialog: DialogServiceMethods,
  { controller, router, store, sectionId, sectionTitle }: {
    controller: SidebarLayoutController
    router: Router
    store: Store<any>
    sectionId: string | null
    sectionTitle: string | null
  }
): void {
  const DialogContent = defineComponent({
    setup () {
      const category = ref(RECOMMENDED_CATEGORY_ID)
      const query = ref('')

      // Same gate as the sidebar itself: nothing the person cannot open is ever offered. Fixed
      // items (Overview) are always in the sidebar, so there is nothing to add.
      const available = computed(() =>
        controller.catalog.entries.filter(entry => !entry.fixed && resolveEntryRouteName(entry, router, store) !== null)
      )

      // Recommendations are worked out once, so adding one marks it Added instead of making it vanish.
      const alreadyAdded = new Set(controller.addedTargets.value)
      const recommended = computed(() => RECOMMENDED_ENTRY_IDS
        .map(id => available.value.find(entry => entry.id === id))
        .filter((entry): entry is NavCatalogEntry => !!entry && !alreadyAdded.has(entry.id))
        .slice(0, RECOMMENDED_LIMIT))

      const categories = computed(() => NAV_CATEGORIES.filter(candidate =>
        candidate.id === RECOMMENDED_CATEGORY_ID ||
        candidate.id === ALL_CATEGORY_ID ||
        available.value.some(entry => entry.category === candidate.id)
      ))

      const inCategory = (id: string) => available.value.filter(entry => entry.category === id)

      const groups = computed<EntryGroup[]>(() => {
        if (query.value.trim()) {
          return [{ entries: searchNavCatalog(available.value, query.value) }]
        }
        if (category.value === RECOMMENDED_CATEGORY_ID) {
          return [{ entries: recommended.value }]
        }
        if (category.value === ALL_CATEGORY_ID) {
          return categories.value
            .filter(candidate => candidate.id !== RECOMMENDED_CATEGORY_ID && candidate.id !== ALL_CATEGORY_ID)
            .map(candidate => ({ heading: candidate.label, entries: inCategory(candidate.id) }))
        }
        // Reports are many; file them under the report categories the Reports Center uses.
        const entries = inCategory(category.value)
        const bySubcategory = new Map<string | undefined, NavCatalogEntry[]>()
        for (const entry of entries) {
          bySubcategory.set(entry.subcategory, [...(bySubcategory.get(entry.subcategory) ?? []), entry])
        }
        return [...bySubcategory.entries()].map(([heading, grouped]) => ({ heading, entries: grouped }))
      })

      const heading = computed(() => {
        if (query.value.trim()) return 'Search results'
        if (category.value === RECOMMENDED_CATEGORY_ID) return 'Pages admins use most'
        if (category.value === ALL_CATEGORY_ID) return 'Explore pages for your sidebar'
        return categories.value.find(candidate => candidate.id === category.value)?.label ?? ''
      })

      // Right after adding is when someone is most likely to want a different name or icon, so an
      // added card offers Edit in place of its Add button. The edit dialog opens over this one.
      const editAdded = (entry: NavCatalogEntry) => {
        const link = controller.entries.value
          .flatMap(candidate => candidate.kind === 'section' ? candidate.links : [candidate])
          .find(candidate => candidate.entry.id === entry.id)
        if (link) {
          editSidebarLink(dialog, controller, link)
        }
      }

      const renderCard = (entry: NavCatalogEntry) => {
        const added = controller.addedTargets.value.has(entry.id)
        return (
          <div class="sidebar-picker-card" key={entry.id}>
            <SidebarTile icon={entry.icon} color={entry.color} />
            <div class="sidebar-picker-card-body">
              <div class="sidebar-picker-card-title">
                <span>{entry.label}</span>
                { added &&
                <span class="sidebar-picker-added">
                  <i class="pi pi-check" /> Added
                </span>
                }
              </div>
              <div class="sidebar-picker-card-detail" title={entry.trail}>{entry.description ?? entry.trail}</div>
            </div>
            { added
              ? <Button
                type="button"
                size="small"
                label="Edit"
                severity="secondary"
                outlined
                rounded
                aria-label={`Edit ${entry.label} in your sidebar`}
                onClick={() => editAdded(entry)}
              />
              : <Button
                type="button"
                size="small"
                label="Add"
                outlined
                rounded
                aria-label={`Add ${entry.label}`}
                onClick={() => controller.addLink(sectionId, entry.id)}
              />
            }
          </div>
        )
      }

      const renderEmpty = () => {
        if (query.value.trim()) {
          return <p class="sidebar-picker-empty">No pages match “{query.value.trim()}”.</p>
        }
        if (category.value === RECOMMENDED_CATEGORY_ID) {
          return <p class="sidebar-picker-empty">You already have everything we'd recommend. Browse All to find more.</p>
        }
        return <p class="sidebar-picker-empty">Nothing here.</p>
      }

      return () => (
        <div class="sidebar-item-picker">
          <nav class="sidebar-picker-rail" aria-label="Categories">
            {categories.value.map(candidate => (
              <button
                type="button"
                key={candidate.id}
                class={['sidebar-picker-rail-item', { 'is-active': !query.value.trim() && category.value === candidate.id }]}
                onClick={() => { category.value = candidate.id; query.value = '' }}
              >
                {candidate.label}
              </button>
            ))}
          </nav>

          <div class="sidebar-picker-main">
            <div class="sidebar-picker-toolbar">
              <h3 class="sidebar-picker-heading">{heading.value}</h3>
              <IconField>
                <InputIcon class="pi pi-search" />
                <InputText v-model={query.value} placeholder="Search" size="small" aria-label="Search pages" autofocus />
              </IconField>
            </div>

            <div class="sidebar-picker-list">
              {groups.value.every(group => group.entries.length === 0)
                ? renderEmpty()
                : groups.value.filter(group => group.entries.length > 0).map(group => (
                  <section key={group.heading ?? '_'} class="sidebar-picker-group">
                    { group.heading && <h4 class="sidebar-picker-group-heading">{group.heading}</h4> }
                    {group.entries.map(renderCard)}
                  </section>
                ))}
            </div>
          </div>
        </div>
      )
    }
  })

  const dialogRef = dialog.open(DialogContent, {
    props: {
      header: sectionTitle ? `Add to ${sectionTitle}` : 'Add to sidebar',
      style: { width: 'min(880px, calc(100vw - 2rem))' },
      pt: { root: 'sidebar-dialog-shell' },
      modal: true,
      closable: true,
      dismissableMask: true,
      closeOnEscape: true
    },
    templates: {
      // DialogService keeps its options in reactive state and only marks `content` raw itself;
      // without markRaw Vue would make this component reactive (and warn about it).
      footer: markRaw(defineComponent({
        setup: () => () => <Button type="button" size="small" label="Done" onClick={() => dialogRef.close()} />
      }))
    }
  })
}
